<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type {
  AutoCompleteSuggestion,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';
import { AppLinkButton } from '@libs/ui';

import {
  EditTripStepType,
  SelectDestinationEmitType,
} from '../../models/editCarTripView.model';
import type {
  EditTripStep,
  SelectDestinationEmit,
} from '../../models/editCarTripView.model';

import ExtendedCarTripSegments from './ExtendedCarTripSegments.vue';
import CollapsedCarTripSegments from './CollapsedCarTripSegments.vue';
import SelectDestinationStep from './SelectDestinationStep.vue';

interface EditCarTripViewContentProps {
  segments: EditTripSegment[];
  initialStep: EditTripStep;
}
const props = defineProps<EditCarTripViewContentProps>();

interface EditCarTripViewContentEmits {
  (eventName: 'add-destination', suggestion: AutoCompleteSuggestion): void;
  (
    eventName: 'add-destination-to-own-stay',
    payload: {
      suggestion: AutoCompleteSuggestion;
      ingredientId: IngredientId;
    }
  ): void;
  (
    eventName: 'change-destination',
    payload: {
      suggestion: AutoCompleteSuggestion;
      ingredientId: IngredientId;
      autocomplete:
        | AutoCompleteType.Destination
        | AutoCompleteType.StartingPosition
        | AutoCompleteType.EndingPosition;
    }
  ): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', segment: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
  (eventName: 'change-step', step: EditTripStep): void;
  (eventName: 'close'): void;
  (
    eventName: 'select-my-position',
    autocompleteType: DestinationAutocompleteType
  ): void;
}
const emit = defineEmits<EditCarTripViewContentEmits>();

const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

const { t } = i18n.global;

const isTripEdited = ref<boolean>(false);
const isPanelCollapsed = ref<boolean>(true);
const step = ref<EditTripStep>(props.initialStep);

const autoCompleteType = ref<
  | AutoCompleteType.Destination
  | AutoCompleteType.StartingPosition
  | AutoCompleteType.EndingPosition
>(AutoCompleteType.Destination);

function selectDestination(payload: SelectDestinationEmit): void {
  switch (payload.type) {
    case SelectDestinationEmitType.AddDestination:
      statsigEventsStore.logTripOverviewEditTripModalDestinationAdded();
      emit('add-destination', payload.suggestion);
      break;

    case SelectDestinationEmitType.AddDestinationToOwnStay:
      emit('add-destination-to-own-stay', {
        suggestion: payload.suggestion,
        ingredientId: payload.ingredientId,
      });
      break;

    case SelectDestinationEmitType.UpdateDestination:
      statsigEventsStore.logTripOverviewEditTripModalDestinationChanged();
      emit('change-destination', {
        suggestion: payload.suggestion,
        ingredientId: payload.ingredientId,
        autocomplete: autoCompleteType.value,
      });
      break;

    default:
      exhaustiveGuard(payload, 'Invalid select destination payload');
  }

  isTripEdited.value = true;

  if (step.value.isInitialStep) {
    emit('close');
  } else {
    openOverviewStep();
  }
}

function changeTripOrder(segments: EditTripSegment[]): void {
  statsigEventsStore.logTripOverviewEditTripModalDestinationRearranged();
  emit('change-trip-order', segments);
  isTripEdited.value = true;
}

function changeNumberOfNights(segment: EditTripSegment): void {
  emit('change-number-of-nights', segment);
}

function removeDestination(segment: EditTripSegment): void {
  emit('remove-destination', segment);
  const TRIP_SEGMENTS_WITH_ONE_MIDDLE_DESTINATION = 3;
  if (props.segments.length === TRIP_SEGMENTS_WITH_ONE_MIDDLE_DESTINATION) {
    isPanelCollapsed.value = true;
  }
  if (props.segments.length === 1) {
    emit('close');
  }
}

function openOverviewStep(): void {
  step.value = {
    stepType: EditTripStepType.Overview,
  };
}

function openAddDestinationStep(stepContext?: {
  isInitialStep: boolean;
}): void {
  step.value = {
    stepType: EditTripStepType.AddDestination,
    isInitialStep: !!stepContext?.isInitialStep,
  };
}

function openUpdateDestinationStep(
  params: {
    segment: EditTripSegment;
    autocomplete:
      | AutoCompleteType.Destination
      | AutoCompleteType.StartingPosition
      | AutoCompleteType.EndingPosition;
  },
  stepContext?: { isInitialStep: boolean }
): void {
  step.value = {
    stepType: EditTripStepType.UpdateDestination,
    currentEditingSegment: params.segment,
    isInitialStep: !!stepContext?.isInitialStep,
  };
  autoCompleteType.value = params.autocomplete;
}

function openAddDestinationToOwnStayStep(
  ownStayDestinationIngredeintId: IngredientId,
  stepContext?: { isInitialStep: boolean }
): void {
  step.value = {
    stepType: EditTripStepType.AddDestinationToOwnStay,
    ownStayDestinationIngredeintId,
    isInitialStep: !!stepContext?.isInitialStep,
  };
}

function selectMyPosition(autocompleteType: DestinationAutocompleteType): void {
  openOverviewStep();
  emit('select-my-position', autocompleteType);
}

watch(step, (currentStep) => emit('change-step', currentStep));

watch(
  () => props.initialStep,
  (initialStep) => (step.value = initialStep)
);

onMounted(() => {
  switch (props.initialStep.stepType) {
    case EditTripStepType.AddDestination:
      openAddDestinationStep({ isInitialStep: true });
      break;

    case EditTripStepType.AddDestinationToOwnStay:
      openAddDestinationToOwnStayStep(
        props.initialStep.ownStayDestinationIngredeintId,
        {
          isInitialStep: true,
        }
      );
      break;

    case EditTripStepType.UpdateDestination:
      openUpdateDestinationStep(
        {
          segment: props.initialStep.currentEditingSegment,
          autocomplete: AutoCompleteType.Destination,
        },
        {
          isInitialStep: true,
        }
      );
      break;

    case EditTripStepType.Overview:
      break;

    default:
      exhaustiveGuard(props.initialStep, 'Invalid initial step');
  }
});
</script>

<template>
  <SelectDestinationStep
    v-if="
      step.stepType === EditTripStepType.AddDestination ||
      step.stepType === EditTripStepType.UpdateDestination ||
      step.stepType === EditTripStepType.AddDestinationToOwnStay
    "
    :autocomplete-type="autoCompleteType"
    :step="step"
    @select-destination="selectDestination"
    @select-my-position="selectMyPosition"
  />
  <template v-else>
    <CollapsedCarTripSegments
      v-if="browserStore.isSmallerThan900px && isPanelCollapsed"
      :segments="props.segments"
      @open-add-destination-step="openAddDestinationStep"
      @expand-panel="isPanelCollapsed = false"
    />
    <template v-else>
      <div
        :class="{
          mobile: browserStore.isSmallerThan900px && !isPanelCollapsed,
        }"
      >
        <ExtendedCarTripSegments
          :segments="props.segments"
          @change-trip-order="changeTripOrder"
          @change-number-of-nights="changeNumberOfNights"
          @change-destination="openUpdateDestinationStep($event)"
          @remove-destination="removeDestination"
          @open-add-destination-step="openAddDestinationStep"
        />
      </div>
      <div
        v-if="browserStore.isSmallerThan900px && !isPanelCollapsed"
        class="actions-wrapper"
      >
        <AppLinkButton
          :text="t('planner.hide_details')"
          size="large"
          right-icon="chevronUp"
          @click="isPanelCollapsed = true"
        />
      </div>
    </template>
  </template>
</template>

<style lang="scss" scoped>
.actions-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(180deg, transparent 0%, var(--color-white) 25%);
  padding: var(--spacing-20) 0;
}
.mobile {
  margin-bottom: var(--spacing-64);
}
</style>
